/**
 * Dawn Theme — Product Enhancements
 * 1. Sticky Add-to-Cart bar (shows when ATC button scrolls out of view)
 * 2. Variant-based media filtering (hide images not matching selected variant)
 */

(function () {
  'use strict';

  /* ═══════════════════════════════════════════════════════════════════════
     1. STICKY ADD-TO-CART BAR
     Shows at the bottom of the viewport once the main ATC button scrolls
     above the fold, hides again when the footer comes into view.
  ═══════════════════════════════════════════════════════════════════════ */

  class StickyAtcBar extends HTMLElement {
    connectedCallback() {
      this._bar = this;
      this._formId = this.getAttribute('data-form-id');
      this._form = document.getElementById(this._formId);
      this._footer = document.querySelector('.shopify-section:last-child, footer, .footer');

      if (!this._form) return;

      // Watch the main product form and footer
      this._observer = new IntersectionObserver(
        this._onIntersect.bind(this),
        { threshold: 0, rootMargin: '0px' }
      );

      this._observer.observe(this._form);
      if (this._footer) this._observer.observe(this._footer);

      // Keep variant title + price in sync
      this._variantTitle = this.querySelector('.sticky-atc__variant');
      this._priceEl = this.querySelector('.sticky-atc__price');

      // Listen for variant changes via Dawn's pubsub
      if (window.subscribe && window.PUB_SUB_EVENTS) {
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const v = event.data?.variant;
          if (!v) return;
          if (this._variantTitle) {
            this._variantTitle.textContent = v.title !== 'Default Title' ? v.title : '';
          }
          if (this._priceEl) {
            this._priceEl.textContent = Shopify.formatMoney
              ? Shopify.formatMoney(v.price, window.theme?.moneyFormat || '${{amount}}')
              : '';
          }
        });
      }
    }

    disconnectedCallback() {
      this._observer && this._observer.disconnect();
    }

    _onIntersect(entries) {
      entries.forEach((entry) => {
        if (entry.target === this._form) {
          // Form scrolled above viewport top = show bar
          this._formHidden = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        }
        if (entry.target === this._footer) {
          // Footer visible = hide bar (don't overlap footer)
          this._footerVisible = entry.isIntersecting;
        }
      });

      const show = this._formHidden && !this._footerVisible;
      this._bar.classList.toggle('is-visible', !!show);
    }
  }

  if (!customElements.get('sticky-atc-bar')) {
    customElements.define('sticky-atc-bar', StickyAtcBar);
  }


  /* ═══════════════════════════════════════════════════════════════════════
     2. VARIANT-BASED MEDIA FILTERING
     Hides product images that belong to other variants.
     Convention (same as Prestige): add #OptionName_OptionValue to image alt.
     Example: "Blue shirt#Color_Blue" → shown only when Color = Blue.

     Dawn already has a simpler version via hide_variants setting, but this
     adds the alt-tag-based granular filtering that Prestige uses.
  ═══════════════════════════════════════════════════════════════════════ */

  function initVariantMediaFilter() {
    const gallery = document.querySelector('[id^="GalleryViewer"]');
    if (!gallery) return;

    const productInfo = document.querySelector('product-info[data-product-id]');
    if (!productInfo) return;

    // Listen for variant changes
    if (window.subscribe && window.PUB_SUB_EVENTS) {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event.data?.variant;
        if (variant) filterMediaForVariant(variant);
      });
    }

    // Also run on page load using the currently selected variant
    const variantInput = document.querySelector('input[name="id"].product-variant-id');
    if (variantInput) {
      // Find variant from page data
      const productData = document.getElementById(`product-json-${productInfo.dataset.productId}`)
        || document.querySelector('[data-product-json]');
      if (productData) {
        try {
          const product = JSON.parse(productData.textContent);
          const selectedVariant = product.variants.find(
            (v) => v.id === parseInt(variantInput.value)
          );
          if (selectedVariant) filterMediaForVariant(selectedVariant);
        } catch (e) {}
      }
    }
  }

  function filterMediaForVariant(variant) {
    const slides = document.querySelectorAll(
      '.product__media-list .product__media-item, .thumbnail-list .thumbnail-list__item'
    );

    slides.forEach((slide) => {
      const img = slide.querySelector('img[alt]');
      if (!img) return;

      const alt = img.getAttribute('alt') || '';
      if (!alt.includes('#')) return; // No filter tag — always visible

      // Parse "#OptionName_OptionValue" from alt
      const tagPart = alt.split('#')[1];
      if (!tagPart) return;

      const [optionName, optionValue] = tagPart.split('_');
      if (!optionName || !optionValue) return;

      // Find this option in the variant
      const product = getProductData();
      if (!product) return;

      const optionIndex = product.options.findIndex(
        (o) => o.toLowerCase() === optionName.toLowerCase()
      );
      if (optionIndex === -1) return;

      const variantValue = variant.options[optionIndex] || '';
      const matches = variantValue.toLowerCase() === optionValue.toLowerCase();

      slide.hidden = !matches;
    });
  }

  function getProductData() {
    if (window._cachedProductData) return window._cachedProductData;
    const el = document.querySelector('[data-product-json], script[type="application/json"][id^="product-json"]');
    if (!el) return null;
    try {
      window._cachedProductData = JSON.parse(el.textContent);
      return window._cachedProductData;
    } catch (e) { return null; }
  }

  // Init on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVariantMediaFilter);
  } else {
    initVariantMediaFilter();
  }

  document.addEventListener('shopify:section:load', () => {
    window._cachedProductData = null;
    initVariantMediaFilter();
  });

})();